import { TransactionMatchSchema } from "@common/schemas/transactionMatch.schema";
import { MatchableItem, MatchItemType } from "./types";

interface LinkedDocumentSuggestionsParams {
    matches: readonly TransactionMatchSchema[] | null | undefined;
    entity: MatchableItem["entity"];
    fallbackAmount: number;
    fallbackDate: Date;
    excludedIds: ReadonlySet<string>;
}

export function linkedDocumentSuggestions({
    matches,
    entity,
    fallbackAmount,
    fallbackDate,
    excludedIds,
}: LinkedDocumentSuggestionsParams): MatchableItem[] {
    const suggestions: MatchableItem[] = [];
    const seenIds = new Set<string>();

    for (const match of matches ?? []) {
        const suggestion = toDocumentSuggestion({
            match,
            entity,
            fallbackAmount,
            fallbackDate,
        });
        if (
            !suggestion ||
            seenIds.has(suggestion.id) ||
            excludedIds.has(suggestion.id)
        ) {
            continue;
        }
        seenIds.add(suggestion.id);
        suggestions.push(suggestion);
    }

    return suggestions;
}

export function accrualMatchedDocumentIds(
    matches: ReadonlyArray<{
        bill?: { id: string } | null;
        invoice?: { id: string } | null;
    }>,
): Set<string> {
    const ids = new Set<string>();
    for (const match of matches) {
        if (match.bill) {
            ids.add(`bill-${match.bill.id}`);
        }
        if (match.invoice) {
            ids.add(`inv-${match.invoice.id}`);
        }
    }
    return ids;
}

function toDocumentSuggestion({
    match,
    entity,
    fallbackAmount,
    fallbackDate,
}: {
    match: TransactionMatchSchema;
    entity: MatchableItem["entity"];
    fallbackAmount: number;
    fallbackDate: Date;
}): MatchableItem | null {
    if (match.billId) {
        return toSuggestion({
            id: `bill-${match.billId}`,
            type: MatchItemType.BILL,
            document: match.bill,
            entity,
            fallbackAmount,
            fallbackDate,
        });
    }
    if (match.invoiceId) {
        return toSuggestion({
            id: `inv-${match.invoiceId}`,
            type: MatchItemType.INVOICE,
            document: match.invoice,
            entity,
            fallbackAmount,
            fallbackDate,
        });
    }
    return null;
}

function toSuggestion({
    id,
    type,
    document,
    entity,
    fallbackAmount,
    fallbackDate,
}: {
    id: string;
    type: MatchItemType.BILL | MatchItemType.INVOICE;
    document: TransactionMatchSchema["bill"];
    entity: MatchableItem["entity"];
    fallbackAmount: number;
    fallbackDate: Date;
}): MatchableItem {
    const accountIds = document?.lineItemAccountId
        ? [document.lineItemAccountId]
        : [];
    const amount = document?.total ?? fallbackAmount;
    const createdAt = document?.createdAt;

    return {
        id,
        date: createdAt instanceof Date ? createdAt : fallbackDate,
        type,
        counterpartyName: document?.counterpartyName ?? null,
        description: document?.invoiceNumber ?? null,
        accountIds,
        cashAccountIds: [],
        accrualAccountIds: accountIds,
        classIds: document?.classIds ?? [],
        amount,
        openAmount: document?.openAmount ?? amount,
        entityId: entity.id,
        entity,
        financialAccountId: null,
    };
}

function linkedDocumentItemId(match: TransactionMatchSchema): string | null {
    if (match.billId) {
        return `bill-${match.billId}`;
    }
    if (match.invoiceId) {
        return `inv-${match.invoiceId}`;
    }
    return null;
}

function unpersistedLinkedDocuments({
    matches,
    persistedItemIds,
    excludedMatchIds,
}: {
    matches: readonly TransactionMatchSchema[] | null | undefined;
    persistedItemIds: ReadonlySet<string>;
    excludedMatchIds?: ReadonlySet<number>;
}): Array<{ itemId: string; matchId: number }> {
    const documents: Array<{ itemId: string; matchId: number }> = [];

    for (const match of matches ?? []) {
        const itemId = linkedDocumentItemId(match);
        if (
            !itemId ||
            excludedMatchIds?.has(match.id) ||
            persistedItemIds.has(itemId)
        ) {
            continue;
        }
        documents.push({ itemId, matchId: match.id });
    }

    return documents;
}

export function suggestedDocumentItemIds({
    matches,
    persistedItemIds,
    excludedMatchIds,
}: {
    matches: readonly TransactionMatchSchema[] | null | undefined;
    persistedItemIds: ReadonlySet<string>;
    excludedMatchIds?: ReadonlySet<number>;
}): string[] {
    return unpersistedLinkedDocuments({
        matches,
        persistedItemIds,
        excludedMatchIds,
    }).map((document) => document.itemId);
}

export function removedSuggestionMatchIds({
    matches,
    persistedItemIds,
    selectedItemIds,
    excludedMatchIds,
}: {
    matches: readonly TransactionMatchSchema[] | null | undefined;
    persistedItemIds: ReadonlySet<string>;
    selectedItemIds: readonly string[];
    excludedMatchIds?: ReadonlySet<number>;
}): number[] {
    const selectedIds = new Set(selectedItemIds);
    return unpersistedLinkedDocuments({
        matches,
        persistedItemIds,
        excludedMatchIds,
    })
        .filter((document) => !selectedIds.has(document.itemId))
        .map((document) => document.matchId);
}
